import { useJobLevels } from "@/queries/use-job-levels.query.ts";
import { useJobProfiles, useJobProfileMove } from "@/queries/use-job-profiles.query.ts";
import { useJobRolesSearch } from "@/queries/use-job-roles.query.ts";
import { ApiJobProfile } from "@/types/job-profile.types.ts";
import { Button, Modal, ModalProps, Select, Stack, useModals } from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

type JobProfileMoveTarget = "role" | "level";

interface JobProfileMoveModalProps extends ModalProps {
  profile: ApiJobProfile;
  target: JobProfileMoveTarget;
  onMoved: (profile: ApiJobProfile) => void;
}

export function JobProfileMoveModal({
  profile,
  target,
  onMoved,
  ...props
}: JobProfileMoveModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;
  const [targetId, setTargetId] = useState<number | null>(null);
  const [roleSearchText, setRoleSearchText] = useState("");
  const [roleFilterText, setRoleFilterText] = useState("");
  const profileMove = useJobProfileMove();
  const { data: roles, isFetching: isLoadingRoles } = useJobRolesSearch(
    { page_number: 1, text: roleFilterText || undefined },
    target === "role"
  );
  const { data: levels, isLoading: isLoadingLevels } = useJobLevels(false, target === "level");
  const { data: profiles, isLoading: isLoadingProfiles } = useJobProfiles();

  useDebounce(() => setRoleFilterText(roleSearchText), 300, [roleSearchText]);

  const options = useMemo(() => {
    if (target === "role") {
      const occupiedRoleIds = new Set(
        (profiles ?? [])
          .filter((item) => item.id !== profile.id && item.level.id === profile.level.id)
          .map((item) => item.role.id)
      );

      return (roles?.items ?? [])
        .filter(
          (role) =>
            !role.is_archived && role.id !== profile.role.id && !occupiedRoleIds.has(role.id)
        )
        .sort((left, right) => left.title.localeCompare(right.title))
        .map((role) => ({ id: role.id, title: role.title }));
    }

    const occupiedLevelIds = new Set(
      (profiles ?? [])
        .filter((item) => item.id !== profile.id && item.role.id === profile.role.id)
        .map((item) => item.level.id)
    );

    return (levels ?? [])
      .filter(
        (level) =>
          !level.is_archived && level.id !== profile.level.id && !occupiedLevelIds.has(level.id)
      )
      .sort(
        (left, right) => left.track.title.localeCompare(right.track.title) || left.sort - right.sort
      )
      .map((level) => ({
        id: level.id,
        title: level.title,
        caption: level.track.title,
      }));
  }, [levels, profile, profiles, roles, target]);

  const handleMove = async () => {
    if (targetId == null) return;

    try {
      const movedProfile = await profileMove.mutateAsync({
        id: profile.id,
        payload: target === "role" ? { job_role_id: targetId } : { job_level_id: targetId },
      });
      close(id);
      onMoved(movedProfile);
    } catch {
      return;
    }
  };

  return (
    <Modal
      {...props}
      title={
        target === "role"
          ? t("core:move_job_profile_to_another_role")
          : t("core:move_job_profile_to_another_level")
      }
      size="md"
      withCloser
      actions={
        <Stack gap="sm">
          <Button
            size="lg"
            disabled={targetId == null}
            isLoading={profileMove.isPending}
            onClick={() => void handleMove()}
          >
            {t("core:move_job_profile")}
          </Button>
          <Button variant="plain" onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Select
        label={target === "role" ? t("core:job_role") : t("core:job_level")}
        emptyTitle={t("common:not_selected")}
        value={targetId}
        options={options}
        searchable
        searchOptions={target === "role" ? options : undefined}
        onSearchOptions={target === "role" ? (text) => setRoleSearchText(text ?? "") : undefined}
        isLoading={isLoadingRoles || isLoadingLevels || isLoadingProfiles}
        onChange={(value) => setTargetId(typeof value === "number" ? value : null)}
      />
    </Modal>
  );
}
